import apiClient from "../utils/apiClient";
import logger from "../utils/logger";
import { extractObjectData } from "../utils/responseUtils";

/**
 * Favorites Service
 * Handles user game favorites functionality
 */
class FavoritesService {
  constructor() {
    this.baseUrl = "/api/casino/favorites";
  }

  /**
   * Get user's favorite games
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of games to fetch
   * @param {number} options.offset - Offset for pagination
   * @returns {Promise<Object>} Favorite games data
   */
  async getFavoriteGames({ limit = 50, offset = 0 } = {}) {
    try {
      logger.info("Fetching user favorite games", { limit, offset });

      const response = await apiClient.get(this.baseUrl, {
        params: { limit, offset },
      });

      logger.info("Favorite games fetched successfully", {
        count: response.data?.games?.length || 0,
        total: response.data?.total,
      });

      return extractObjectData(response, { games: [], total: 0 });
    } catch (error) {
      logger.error("Failed to fetch favorite games", error);
      throw error;
    }
  }

  /**
   * Add a game to favorites
   * @param {string} gameCode - Game code to add to favorites
   * @returns {Promise<Object>} Success response
   */
  async addToFavorites(gameCode) {
    try {
      logger.info("Adding game to favorites", { gameCode });

      const response = await apiClient.post(`${this.baseUrl}/${gameCode}`);

      logger.info("Game added to favorites successfully", {
        gameCode,
        message: response.data?.message,
      });

      return response.data || { favorited: true };
    } catch (error) {
      logger.error("Failed to add game to favorites", error, { gameCode });
      throw error;
    }
  }

  /**
   * Remove a game from favorites
   * @param {string} gameCode - Game code to remove from favorites
   * @returns {Promise<Object>} Success response
   */
  async removeFromFavorites(gameCode) {
    try {
      logger.info("Removing game from favorites", { gameCode });

      const response = await apiClient.delete(`${this.baseUrl}/${gameCode}`);

      logger.info("Game removed from favorites successfully", {
        gameCode,
        message: response.data?.message,
      });

      return response.data || { favorited: false };
    } catch (error) {
      logger.error("Failed to remove game from favorites", error, { gameCode });
      throw error;
    }
  }

  /**
   * Toggle favorite status for a game
   * @param {string} gameCode - Game code to toggle
   * @param {boolean} currentStatus - Current favorite status
   * @returns {Promise<Object>} Updated status
   */
  async toggleFavorite(gameCode, currentStatus) {
    try {
      if (currentStatus) {
        return await this.removeFromFavorites(gameCode);
      } else {
        return await this.addToFavorites(gameCode);
      }
    } catch (error) {
      logger.error("Failed to toggle favorite status", error, {
        gameCode,
        currentStatus,
      });
      throw error;
    }
  }

  /**
   * Check if a specific game is favorited
   * @param {string} gameCode - Game code to check
   * @returns {Promise<boolean>} Whether the game is favorited
   */
  async isFavorited(gameCode) {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/status/${gameCode}`,
      );
      return response.data.favorited;
    } catch (error) {
      logger.error("Failed to check favorite status", error, { gameCode });
      return false;
    }
  }

  // Note: getBulkFavoriteStatus removed - we now load all favorites once for efficiency

  /**
   * Get favorite games count for a user
   * @returns {Promise<number>} Number of favorite games
   */
  async getFavoriteCount() {
    try {
      const response = await this.getFavoriteGames({ limit: 1 });
      return response.total || 0;
    } catch (error) {
      logger.error("Failed to get favorite count", error);
      return 0;
    }
  }
}

// Export singleton instance
const favoritesService = new FavoritesService();
export default favoritesService;
